/**
 * Search-match highlighting, shared by every admin list with a search box. `highlightSegments` is
 * the pure half; `components/ui/HighlightText.vue` wraps the matching segments in <mark>.
 */

export type HighlightSegment = { text: string; match: boolean };

/**
 * Split `text` into consecutive segments, marking every case-insensitive match of `query`. The
 * query is regex-escaped and matched against the original string, so IP dots match as-is.
 */
export function highlightSegments(
  text: string | null | undefined,
  query: string | null | undefined,
): HighlightSegment[] {
  const value = text ?? "";
  const q = (query ?? "").trim();
  if (!q || !value) return [{ text: value, match: false }];

  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(escaped, "gi");
  const segments: HighlightSegment[] = [];
  let last = 0;
  for (let m = re.exec(value); m !== null; m = re.exec(value)) {
    if (m.index > last) segments.push({ text: value.slice(last, m.index), match: false });
    // m[0] is taken from the original string, so the highlight preserves case.
    segments.push({ text: m[0], match: true });
    last = m.index + m[0].length;
  }
  if (last < value.length) segments.push({ text: value.slice(last), match: false });
  return segments;
}

/**
 * Lower-cased haystack from the parts of a row, dropping empty and nullish parts so a row never
 * matches on the gap between two others. Build it from what the row shows: a hit on a hidden field
 * highlights nothing, which reads as a bug.
 */
export function searchHaystack(parts: (string | null | undefined)[]): string {
  return parts
    .filter((p): p is string => Boolean(p))
    .join(" ")
    .toLocaleLowerCase();
}

/** Whether a haystack matches the query. An empty query matches everything. */
export function matchesQuery(haystack: string, query: string | null | undefined): boolean {
  const term = (query ?? "").trim().toLocaleLowerCase();
  return term === "" || haystack.includes(term);
}
